import { fftRadix2 } from './fft'

export const DEFAULT_THRESHOLDS = {
  minVoiceRatio: 0.15,
  maxPeakDb: -0.5,
  maxClippedSamples: 100,
  minVoiceDb: -42,
  minSnr: 12,
  maxFlatness: 0.5
} as const

export type MicCheckThresholds = typeof DEFAULT_THRESHOLDS

export const SILENCE_MAX_ABS = 1e-4
const ANALYSIS_FRAME_SIZE = 2048

export type MicCheckIssue = 'no-voice' | 'clip' | 'quiet' | 'noise' | 'no-signal' | null

export interface MicCheckMetrics {
  noiseFloorDb: number
  voiceDb: number
  peakDb: number
  snr: number
  voiceRatio: number
  clippedSamples: number
  flatness: number
}

export interface MicCheckResult extends MicCheckMetrics {
  ok: boolean
  issue: MicCheckIssue
}

export function rms(a: Float32Array): number {
  if (a.length === 0) return 0
  let sum = 0
  for (let i = 0; i < a.length; i++) sum += a[i] * a[i]
  return Math.sqrt(sum / a.length)
}

export function peak(a: Float32Array): number {
  let p = 0
  for (let i = 0; i < a.length; i++) {
    const v = Math.abs(a[i])
    if (v > p) p = v
  }
  return p
}

export function clippedCount(a: Float32Array): number {
  let c = 0
  for (let i = 0; i < a.length; i++) if (Math.abs(a[i]) > 0.99) c++
  return c
}

const largestPow2 = (n: number): number => {
  let p = 1
  while (p * 2 <= n) p *= 2
  return p
}

export function spectralFlatness(frame: Float32Array): number {
  const n = largestPow2(frame.length)
  if (n < 2) return 0
  const re = new Float64Array(n)
  const im = new Float64Array(n)
  for (let i = 0; i < n; i++) {
    const w = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (n - 1))
    re[i] = frame[i] * w
  }
  fftRadix2(re, im)
  const half = n >> 1
  let logSum = 0
  let sum = 0
  let count = 0
  for (let i = 1; i < half; i++) {
    const mag = Math.sqrt(re[i] * re[i] + im[i] * im[i]) + 1e-12
    logSum += Math.log(mag)
    sum += mag
    count++
  }
  if (count === 0) return 0
  const geoMean = Math.exp(logSum / count)
  const arithMean = sum / count
  return arithMean > 0 ? geoMean / arithMean : 0
}

export function linearToDb(x: number): number {
  return x <= 0 ? -Infinity : 20 * Math.log10(x)
}

export function evaluate(
  rmsFrames: number[],
  peakFrames: number[],
  clippedSamples: number,
  flatnessFrames: number[] = [],
  thresholds: Partial<MicCheckThresholds> = {}
): MicCheckResult {
  const t = { ...DEFAULT_THRESHOLDS, ...thresholds }

  const sorted = [...rmsFrames].sort((a, b) => a - b)
  const noiseFloorLin = sorted[Math.floor(sorted.length * 0.1)] || 0.001
  const noiseFloorDb = linearToDb(noiseFloorLin)

  const peakLin = peakFrames.length ? Math.max(...peakFrames) : 0
  const peakDb = linearToDb(peakLin)

  const voiceThresholdLin = noiseFloorLin * 3
  const voicedRms: number[] = []
  let flatnessSum = 0
  let flatnessVoiced = 0
  for (let i = 0; i < rmsFrames.length; i++) {
    if (rmsFrames[i] > voiceThresholdLin) {
      voicedRms.push(rmsFrames[i])
      if (i < flatnessFrames.length) {
        flatnessSum += flatnessFrames[i]
        flatnessVoiced++
      }
    }
  }
  voicedRms.sort((a, b) => a - b)
  const voiceSlice = voicedRms.slice(Math.floor(voicedRms.length * 0.75))
  const voiceAvgLin = voiceSlice.length ? voiceSlice.reduce((a, b) => a + b, 0) / voiceSlice.length : noiseFloorLin
  const voiceDb = linearToDb(voiceAvgLin)

  const snr = voiceDb - noiseFloorDb
  const voiceRatio = rmsFrames.length ? voicedRms.length / rmsFrames.length : 0
  const flatness = flatnessVoiced ? flatnessSum / flatnessVoiced : 0

  const metrics: MicCheckMetrics = { noiseFloorDb, voiceDb, peakDb, snr, voiceRatio, clippedSamples, flatness }

  if (voiceRatio < t.minVoiceRatio) return { ...metrics, ok: false, issue: 'no-voice' }
  if (peakDb > t.maxPeakDb || clippedSamples > t.maxClippedSamples) return { ...metrics, ok: false, issue: 'clip' }
  if (voiceDb < t.minVoiceDb) return { ...metrics, ok: false, issue: 'quiet' }
  if (snr < t.minSnr || flatness > t.maxFlatness) return { ...metrics, ok: false, issue: 'noise' }
  return { ...metrics, ok: true, issue: null }
}

export function analyzeBuffer(samples: Float32Array, thresholds: Partial<MicCheckThresholds> = {}): MicCheckResult {
  if (samples.length === 0 || peak(samples) < SILENCE_MAX_ABS) {
    return {
      noiseFloorDb: -Infinity,
      voiceDb: -Infinity,
      peakDb: -Infinity,
      snr: 0,
      voiceRatio: 0,
      clippedSamples: 0,
      flatness: 0,
      ok: false,
      issue: 'no-signal'
    }
  }

  const rmsFrames: number[] = []
  const peakFrames: number[] = []
  const flatnessFrames: number[] = []
  let clipped = 0
  for (let i = 0; i < samples.length; i += ANALYSIS_FRAME_SIZE) {
    const frame = samples.subarray(i, i + ANALYSIS_FRAME_SIZE)
    rmsFrames.push(rms(frame))
    peakFrames.push(peak(frame))
    flatnessFrames.push(spectralFlatness(frame))
    clipped += clippedCount(frame)
  }

  return evaluate(rmsFrames, peakFrames, clipped, flatnessFrames, thresholds)
}
